/**
 * Authentication API Service
 * Handles frontend API calls for user authentication
 *
 * AUTH MODEL: httpOnly cookies only. No localStorage token storage.
 * All fetch calls use credentials: 'include' so the browser sends cookies automatically.
 */

import {
  checkAuthStatus,
  testZendeskConnection as sharedTestZendeskConnection,
  clearAuthData,
  createApiHeaders,
  handleApiError
} from '../utils/shared.js';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.triagegenius.com';
console.log('DEBUG AuthAPI: API_BASE_URL set to:', API_BASE_URL);
console.log('DEBUG AuthAPI: VITE_API_URL env var:', import.meta.env.VITE_API_URL);

class AuthAPI {
  /**
   * Register a new user
   */
  static async register(email, password, name, plan, promo) {
    try {
      console.log('DEBUG AuthAPI.register: Starting request to:', `${API_BASE_URL}/api/auth/register`);
      console.log('DEBUG AuthAPI.register: Request data:', { email, name, plan, promo, password: '***' });

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, plan, promo }),
        credentials: 'include'
      });

      console.log('DEBUG AuthAPI.register: Response status:', response.status);
      console.log('DEBUG AuthAPI.register: Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      console.log('DEBUG AuthAPI.register: Content-Type:', contentType);

      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('AuthAPI.register: Non-JSON response:', textResponse);
        throw new Error(`Server returned non-JSON response: ${textResponse.substring(0, 200)}`);
      }

      const data = await response.json();
      console.log('DEBUG AuthAPI.register: Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store token if provided (for immediate authentication)
      if (data.token) {
        // Token is set via httpOnly cookie — no localStorage storage needed
        console.log('🔧 DEBUG AuthAPI.register: Token stored in localStorage');
      }

      return data;
    } catch (error) {
      console.error('AuthAPI.register error:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  /**
   * Login user
   */
  static async login(email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        // Check for force password reset
        if (data.error === 'password_reset_required') {
          const error = new Error('password_reset_required');
          error.code = 'password_reset_required';
          throw error;
        }
        throw new Error(data.error || 'Login failed');
      }

      // Store token in localStorage as backup
      if (data.token) {
        // Token is set via httpOnly cookie — no localStorage storage needed
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  static async logout() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      // Clear non-auth localStorage regardless of response
      await clearAuthData();

      return data;
    } catch (error) {
      console.error('Logout error:', error);

      // Clear non-auth localStorage even if API call fails
      await clearAuthData();

      throw error;
    }
  }

  /**
   * Verify current session via httpOnly cookie
   */
  static async verifyToken() {
    try {
      console.log('DEBUG AuthAPI.verifyToken: Making API call to verify token');

      const response = await fetch(`${API_BASE_URL}/api/auth/verify-token`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Token verification failed');
      }

      console.log('DEBUG AuthAPI.verifyToken: Token verified successfully');
      return data;

    } catch (error) {
      console.error('Token verification error:', error);
      throw error;
    }
  }

  /**
   * Get current user
   */
  static async getCurrentUser() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get user data');
      }

      return data.user;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Change password
   */
  static async changePassword(currentPassword, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currentPassword, newPassword }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Password change failed');
      }

      return data;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }

  /**
   * Test Zendesk connection
   */
  static async testZendeskConnection(zendeskData) {
    return await sharedTestZendeskConnection(zendeskData);
  }

  /**
   * Register user with trial account (combined QuickStart flow)
   */
  static async registerWithTrial(registrationData) {
    try {
      console.log('DEBUG: Making registerWithTrial request to:', `${API_BASE_URL}/api/auth/register-with-trial`);
      console.log('DEBUG: Request data:', registrationData);

      const response = await fetch(`${API_BASE_URL}/api/auth/register-with-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData),
        credentials: 'include'
      });

      console.log('DEBUG: Response status:', response.status);

      const data = await response.json();
      console.log('DEBUG: Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Failed to create account');
      }

      // Store token for immediate authentication
      if (data.token) {
        // Token is set via httpOnly cookie — no localStorage storage needed
      }

      return data;
    } catch (error) {
      console.error('Register with trial error:', error);
      throw error;
    }
  }

  /**
   * Create trial account (no authentication required for QuickStart)
   */
  static async createTrial(zendesk = null) {
    try {
      const requestBody = {};

      // Include Zendesk connection details if provided
      if (zendesk) {
        requestBody.zendesk = zendesk;
      }

      console.log('DEBUG: Making createTrial request to:', `${API_BASE_URL}/api/auth/create-trial`);
      console.log('DEBUG: Request body:', requestBody);

      const response = await fetch(`${API_BASE_URL}/api/auth/create-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      console.log('DEBUG: Response status:', response.status);
      console.log('DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('DEBUG: Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Failed to create trial account');
      }

      // Store token for immediate authentication
      if (data.token) {
        // Token is set via httpOnly cookie — no localStorage storage needed
      }

      return data.account;
    } catch (error) {
      console.error('Create trial error:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Run database migrations (for initial setup)
   */
  static async runMigrations() {
    try {
      console.log('DEBUG AuthAPI.runMigrations: Making request to:', `${API_BASE_URL}/api/migrate`);

      const response = await fetch(`${API_BASE_URL}/api/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      console.log('DEBUG AuthAPI.runMigrations: Response status:', response.status);
      console.log('DEBUG AuthAPI.runMigrations: Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response has content
      const responseText = await response.text();
      console.log('DEBUG AuthAPI.runMigrations: Response text:', responseText);

      if (!responseText.trim()) {
        throw new Error('Empty response from migration endpoint');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', responseText);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }

      console.log('DEBUG AuthAPI.runMigrations: Parsed data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Migration failed');
      }

      return data;
    } catch (error) {
      console.error('Migration error:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  /**
   * Check if user is authenticated via httpOnly cookie
   * Returns a Promise (async) unlike the old synchronous version.
   */
  static async checkAuth() {
    return await checkAuthStatus();
  }

  /**
   * Send email verification
   */
  static async sendVerification() {
    try {
      console.log('DEBUG AuthAPI.sendVerification: Making request to:', `${API_BASE_URL}/api/auth/send-verification`);
      const response = await fetch(`${API_BASE_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      console.log('DEBUG AuthAPI.sendVerification: Response status:', response.status);
      console.log('DEBUG AuthAPI.sendVerification: Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      console.log('DEBUG AuthAPI.sendVerification: Content-Type:', contentType);

      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('AuthAPI.sendVerification: Non-JSON response:', textResponse);
        throw new Error(`Server returned non-JSON response: ${textResponse.substring(0, 200)}`);
      }

      const data = await response.json();
      console.log('DEBUG AuthAPI.sendVerification: Response data:', data);

      if (!data.success) {
        throw new Error(data.error || 'Failed to send verification email');
      }

      return data;
    } catch (error) {
      console.error('AuthAPI.sendVerification error:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  /**
   * Verify email with token
   */
  static async verifyEmail(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Email verification failed');
      }

      return data;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  }

  /**
   * Resend verification email
   */
  static async resendVerification(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      return data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  }

  /**
   * Complete post-registration setup (backend reads from KV)
   */
  static async completeSetup() {
    try {
      console.log('DEBUG AuthAPI.completeSetup: Making API call');

      const response = await fetch(`${API_BASE_URL}/api/auth/complete-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Setup completion failed');
      }

      console.log('DEBUG AuthAPI.completeSetup: Setup completed successfully');
      return data;

    } catch (error) {
      console.error('Complete setup error:', error);
      throw error;
    }
  }

  /**
   * Get saved setup progress from server
   */
  static async getSetupProgress() {
    const response = await fetch(`${API_BASE_URL}/api/setup/progress`, {
      credentials: 'include'
    });
    const data = await response.json();
    return data.progress || {};
  }

  /**
   * Save setup progress step to server
   */
  static async saveSetupProgress(step, data) {
    const response = await fetch(`${API_BASE_URL}/api/setup/progress`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step, data }),
      credentials: 'include'
    });
    return await response.json();
  }

  /**
   * Request password reset
   */
  static async requestPasswordReset(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return data;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  static async resetPassword(token, email, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, email, newPassword }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  /**
   * Verify password reset token
   */
  static async verifyResetToken(token, email) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-reset-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, email }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to verify reset token');
      }

      return data;
    } catch (error) {
      console.error('Verify reset token error:', error);
      throw error;
    }
  }
}

export default AuthAPI;
